/*!
 * mapWarp.js — map:, the arithmetic: a flat thing bent onto four points
 * © 2026 Rob Canning — GPLv3
 *
 * A projector rarely meets a surface square on: a picture thrown at a wall,
 * a box, a chair comes out as a quadrilateral. map: says where a thing's four
 * corners land -- top left, top right, bottom right, bottom left -- and the
 * thing is bent in perspective to meet them (a homography: straight lines
 * stay straight, a grid shrinks the way a floor does going away).
 *
 *   homography(quad)          the unit square onto quad: { a, b, c, d, e, f, g, h }
 *   warpPoint(H, u, v)        a point of the square, where it lands
 *   rectOnto(rect, quad)      a rectangle [x, y, w, h] onto quad: a function (x, y) -> [x, y]
 *   cssWarp(w, h, quad)       the CSS matrix3d that bends a w x h box onto quad
 *                             (transform-origin 0 0; quad in the box's own px)
 *   cssWarpFrom(rect, quad)   the same, a part [x, y, w, h] of the box onto quad
 *   mapPoints(v)              map:'s list as [[x, y] ...], or null
 *
 * No DOM here: the cues (shape.js, the pictures) say what a thing is and
 * where its points are, and node tests check the bending.
 */

/**
 * The unit square's corners (0,0) (1,0) (1,1) (0,1) onto quad's four points,
 * in that order: x' = (a u + b v + c) / (g u + h v + 1), y' = (d u + e v + f) / (...).
 * A parallelogram is the plain case (g = h = 0). Heckbert's closed form.
 */
export function homography(quad) {
  const [[x0, y0], [x1, y1], [x2, y2], [x3, y3]] = quad;
  const sx = x0 - x1 + x2 - x3, sy = y0 - y1 + y2 - y3;
  if (Math.abs(sx) < 1e-12 && Math.abs(sy) < 1e-12) {
    return { a: x1 - x0, b: x3 - x0, c: x0, d: y1 - y0, e: y3 - y0, f: y0, g: 0, h: 0 };
  }
  const dx1 = x1 - x2, dx2 = x3 - x2, dy1 = y1 - y2, dy2 = y3 - y2;
  const den = dx1 * dy2 - dx2 * dy1 || 1e-12;           // three points in a line: as near as it gets
  const g = (sx * dy2 - dx2 * sy) / den;
  const h = (dx1 * sy - sx * dy1) / den;
  return {
    a: x1 - x0 + g * x1, b: x3 - x0 + h * x3, c: x0,
    d: y1 - y0 + g * y1, e: y3 - y0 + h * y3, f: y0,
    g, h,
  };
}

/** Where (u, v) of the unit square lands. */
export function warpPoint(H, u, v) {
  const w = H.g * u + H.h * v + 1;
  return [(H.a * u + H.b * v + H.c) / w, (H.d * u + H.e * v + H.f) / w];
}

/** The rectangle [x, y, w, h] onto quad: a point of it, where it lands. */
export function rectOnto([rx, ry, rw, rh], quad) {
  const H = homography(quad);
  return (x, y) => warpPoint(H, (x - rx) / (rw || 1), (y - ry) / (rh || 1));
}

/**
 * The CSS transform that bends a w x h box (transform-origin 0 0) onto quad,
 * quad in the box's own px. The homography with u = x / w, v = y / h, as a
 * column-major matrix3d.
 */
export function cssWarp(w, h, quad) {
  return cssWarpFrom([0, 0, w, h], quad);
}

/**
 * The same for a part of the box, [x, y, w, h] of its own px: that part lands
 * on quad, the rest of the box bent with it (a sound's canvas reaching past
 * its box). u = (x - rx) / rw, v = (y - ry) / rh folded into the matrix.
 */
export function cssWarpFrom([rx, ry, rw, rh], quad) {
  const H = homography(quad);
  const W = rw || 1, Hh = rh || 1;
  const a = H.a / W, b = H.b / Hh, d = H.d / W, e = H.e / Hh, g = H.g / W, h = H.h / Hh;
  const m = [
    a, d, 0, g,
    b, e, 0, h,
    0, 0, 1, 0,
    H.c - a * rx - b * ry, H.f - d * rx - e * ry, 0, 1 - g * rx - h * ry,
  ];
  return `matrix3d(${m.map(n => +n.toPrecision(12)).join(", ")})`;
}

/**
 * map:'s value as points: [[x, y], ...] of numbers, two or more (a line's ends) -- or null
 * (map:1, map:points, map:0, anything else).
 */
export function mapPoints(v) {
  if (!Array.isArray(v) || v.length < 2) return null;
  const pts = v.map(p => (Array.isArray(p) && p.length >= 2 ? [Number(p[0]), Number(p[1])] : null));
  return pts.every(p => p && Number.isFinite(p[0]) && Number.isFinite(p[1])) ? pts : null;
}

/** Points as map: writes them: [[0.1, 0.2], [0.9, 0.2], ...], four decimals. */
export function mapText(pts) {
  return `[${pts.map(([x, y]) => `[${+x.toFixed(4)}, ${+y.toFixed(4)}]`).join(", ")}]`;
}
